/**
 * Draft Review — its own full page, one of the three Draft Board sections
 * (see pages/DraftBoard.jsx, the landing/picker page this is reached from).
 * Draft Board rebuild Stage 3 — see engineering/ARCHIVE/completed-prompts/JAI_DraftBoard_Rebuild_Prompt.md.
 *
 * Reads only GET /api/leagues/:id/draft/review, which itself reads only
 * DraftResult — never the live feed. All tiers, every pick: this tab is
 * deliberately not round-capped (see requireDraftRoundAccess's own comment
 * on the backend). The one Claude call this tab can trigger happens lazily
 * on first load and is cached from then on — Refresh is the only way to
 * re-bill it.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLeagues } from '../../context/LeagueContext'
import { draftBoardApi } from '../../services/api'
import AppShell from '../../components/AppShell'
import ToolHeader from '../../components/ToolHeader'
import PlayerPhoto from '../../components/PlayerPhoto'
import StickyAskBar from '../../components/StickyAskBar'
import { JAI_FONTS } from '../../components/JaiBrand'
import { V2, V2Card, V2Emphasis, V2List, V2ListRow, PosChip, solidChip, pctTone, RefreshButton } from '../../components/JaiV2'
import { JAIMascot } from '../../components/JAIMascot'
import { teamColor } from '../../config/teamColors'
import { shareText } from '../../services/share'
import { trackEvent } from '../../analytics'
import { useMinWidth } from '../../hooks/useMinWidth'
import { IS_NATIVE_APP } from '../../services/runtime'

const TAG_COLOR = { sleeper: V2.blue, safe: V2.success, bust: V2.error }
const TAG_LABEL = { sleeper: 'Sleeper', safe: 'Safe pick', bust: 'Bust risk' }

// Draft Review's own value language — 'steal'/'value'/'reach' — maps 1:1
// from the SAME good/okay/bad grade Live Draft's ticker showed during the
// draft (draftReviewService.classifyPick), so these colours must match the
// ticker's good/okay/bad colours pick-for-pick even though the words differ.
const CLASS_COLOR = { steal: V2.success, value: V2.warning, reach: V2.error }
const CLASS_LABEL = { steal: 'Steal', value: 'Fair value', reach: 'Reach' }

const REC_LABEL = { keep: 'Keep', trade: 'Trade', drop: 'Drop' }

// ── keep / trade / drop icon ──────────────────────────────────────────────

function RecommendationIcon({ rec }) {
  if (rec === 'trade') {
    return (
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <path d="M2 5.5h9M8 2.5l3 3-3 3M14 10.5H5M8 13.5l-3-3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (rec === 'drop') {
    return (
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <path d="M8 2.5v7M4.5 6.5 8 10l3.5-3.5M3 13.5h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path d="M3 8.3 6.2 11.5 13 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function RecommendationBadge({ rec, leagueId, playerName }) {
  const navigate = useNavigate()
  const color = rec === 'trade' ? V2.blue : rec === 'drop' ? V2.error : V2.success
  const chip = solidChip(color)
  const clickable = rec === 'trade' || rec === 'drop'

  const go = () => {
    if (rec === 'trade') navigate(`/league/${leagueId}/trade/analyse?give=${encodeURIComponent(playerName)}`)
    if (rec === 'drop') navigate(`/league/${leagueId}/waiver?drop=${encodeURIComponent(playerName)}`)
  }

  return (
    <button
      onClick={clickable ? go : undefined}
      disabled={!clickable}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 800, ...chip,
        border: 'none', borderRadius: 999, padding: '5px 11px',
        cursor: clickable ? 'pointer' : 'default',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}
    >
      <RecommendationIcon rec={rec} />
      {REC_LABEL[rec] || rec}
    </button>
  )
}

// ── Overall performance block (3.1) ───────────────────────────────────────

function StatCell({ label, value, sub }) {
  return (
    <div style={{ flex: '1 1 96px', textAlign: 'center', padding: '4px 6px', minWidth: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 800, fontFamily: JAI_FONTS.mono, color: 'white', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginTop: 6 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function OverviewBlock({ overall, leagueId, leagueType }) {
  const navigate = useNavigate()

  // Native-only (Guideline 4.2). Headline + a couple of lines + "via JAI" —
  // no logo, no link to pricing.
  const handleShare = () => {
    const lines = [`My draft grade, via JAI`]
    lines.push(`${overall.overallPicksOfValue} picks of value out of ${overall.totalPicks}`)
    lines.push(`${overall.steals} steals, ${overall.reaches} reaches`)
    if (overall.onTaskPercent != null) lines.push(`${overall.onTaskPercent}% on strategy`)
    lines.push('via JAI')
    shareText(lines.join('\n'))
  }

  return (
    <V2Emphasis style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <StatCell label="Picks of Value" value={overall.overallPicksOfValue} sub={`of ${overall.totalPicks}`} />
        <StatCell label="Value Picks" value={overall.valuePicks} />
        <StatCell label="Steals" value={overall.steals} />
        <StatCell label="Reaches" value={overall.reaches} />
        {overall.onTaskPercent != null ? (
          <StatCell label="On Task" value={<span style={{ color: pctTone(overall.onTaskPercent) }}>{overall.onTaskPercent}%</span>} />
        ) : (
          <div style={{ flex: '1 1 96px', textAlign: 'center', padding: '4px 6px', minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>No strategy locked</div>
            <button
              onClick={() => navigate(`/league/${leagueId}/draft/strategy`)}
              style={{ marginTop: 4, background: 'none', border: 'none', color: 'white', textDecoration: 'underline', fontSize: 10.5, cursor: 'pointer', padding: 0 }}
            >
              Set your strategy &rarr;
            </button>
          </div>
        )}
      </div>
      {(leagueType === 'dynasty' || leagueType === 'keeper') && (
        <div data-testid="league-type-note" style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.15)', fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
          {leagueType === 'dynasty'
            ? "This is a dynasty league. Value grades compare each pick with this season's redraft market, so a young player graded a reach can still be a good long-term pick."
            : 'This is a keeper league. Value grades compare each pick with this season\'s market; a pick can still be worth it as a keeper.'}
        </div>
      )}
      {overall.beyondStrategyRounds && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.15)', fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
          You were on your own from round {overall.strategyMaxRound + 1} — those picks are graded honestly below, without advice by then.
        </div>
      )}
      {IS_NATIVE_APP && (
        <button
          onClick={handleShare}
          style={{
            marginTop: 16, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
            color: 'white', borderRadius: 9, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Share
        </button>
      )}
    </V2Emphasis>
  )
}

// ── Pick list (3.2) ────────────────────────────────────────────────────────

function ReviewPickRow({ pick, sport, leagueId, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen)
  const team = teamColor(pick.team, sport)
  const tagChip = solidChip(TAG_COLOR[pick.tag] || V2.mute)
  const classChip = solidChip(CLASS_COLOR[pick.classification] || V2.mute)

  return (
    <>
      <V2ListRow onClick={() => setOpen((v) => !v)} style={{ cursor: 'pointer' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: V2.faint, fontFamily: JAI_FONTS.mono, flexShrink: 0, width: 76 }}>
          R{pick.round} · P{pick.pickInRound}
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: V2.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pick.name}
        </span>
        {pick.position && <PosChip>{pick.position}</PosChip>}
        {pick.team && (
          <span style={{ fontSize: 10.5, fontWeight: 700, color: team.ink, background: team.primary, borderRadius: 999, padding: '2px 8px', flexShrink: 0 }}>
            {pick.team}
          </span>
        )}
        <span style={{
          fontSize: 9.5, fontWeight: 800, ...classChip,
          borderRadius: 6, padding: '3px 8px', flexShrink: 0, letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          {CLASS_LABEL[pick.classification] || pick.classification}
        </span>
        <span style={{ color: V2.faint, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms', flexShrink: 0 }}>&#9662;</span>
      </V2ListRow>

      {open && (
        <V2ListRow style={{ alignItems: 'flex-start' }}>
          <PlayerPhoto name={pick.name} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 9.5, fontWeight: 800, ...tagChip, borderRadius: 6, padding: '2px 7px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {TAG_LABEL[pick.tag] || pick.tag}
              </span>
              {pick.adpAtDraft != null && (
                <span style={{ fontSize: 11, color: V2.mute, fontFamily: JAI_FONTS.mono }}>ADP {pick.adpAtDraft} at draft</span>
              )}
            </div>
            <div style={{ fontSize: 12.5, color: V2.mute, lineHeight: 1.75 }}>
              {pick.relevanceToStrategy && <div><strong style={{ color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Strategy fit</strong> &nbsp;{pick.relevanceToStrategy}</div>}
              {pick.valueDetermination && <div><strong style={{ color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Value</strong> &nbsp;{pick.valueDetermination}</div>}
              {pick.contribution && <div><strong style={{ color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Role</strong> &nbsp;{pick.contribution}</div>}
            </div>
            <div style={{ marginTop: 10 }}>
              <RecommendationBadge rec={pick.recommendation} leagueId={leagueId} playerName={pick.name} />
            </div>
          </div>
        </V2ListRow>
      )}
    </>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function DraftReview() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { leagues } = useLeagues()
  const league = leagues.find((l) => l.id === Number(id))
  const sport = (league?.sport || 'nba').toLowerCase()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  // { message, hasStrategy } | null — hasStrategy is only present on the
  // "nothing to review yet" 404 (see getDraftReview); a real failure (502/500)
  // leaves it undefined so the CTA below doesn't guess at a stage that isn't
  // actually the problem.
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const wide = useMinWidth(820)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await draftBoardApi.getDraftReview(id)
      setData(res.data)
      trackEvent('draft_review_viewed', { league_type: res.data?.leagueType || 'redraft', has_strategy: !!res.data?.strategy })
    } catch (err) {
      setError({
        message: err.response?.data?.error || 'Could not load your draft review.',
        hasStrategy: err.response?.data?.hasStrategy,
        draftState: err.response?.data?.draftState,
      })
      trackEvent('draft_review_unavailable', { reason: err.response?.data?.draftState || (err.response?.data?.hasStrategy === false ? 'no_strategy' : err.response?.data?.hasStrategy ? 'not_ready' : 'error') })
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const res = await draftBoardApi.refreshDraftReview(id)
      setData(res.data)
      setError(null)
    } catch (err) {
      // Surface it instead of silently keeping stale data on screen — a
      // refresh that fails needs to be visible, not indistinguishable from
      // a refresh that did nothing.
      setError({
        message: err.response?.data?.error || 'Could not refresh your draft review.',
        hasStrategy: err.response?.data?.hasStrategy,
        draftState: err.response?.data?.draftState,
      })
    }
    setRefreshing(false)
  }

  return (
    <AppShell>
      <div style={{ padding: wide ? '30px 40px' : '20px 16px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: 100 }}>
          <ToolHeader
            leagueName={league?.name}
            title="Draft Review"
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
            backRight={{ label: 'Go to Draft Room', to: `/league/${id}/draft` }}
          />

          {data && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -14, marginBottom: 14 }}>
              <RefreshButton onClick={handleRefresh} loading={refreshing} credits="draftReview" style={{ fontSize: 12.5, padding: '5px 12px' }} />
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <JAIMascot size={56} state="thinking" animate />
              </div>
              <div style={{ fontSize: 12.5, color: V2.faint, marginTop: 10 }}>Grading your draft…</div>
            </div>
          )}

          {!loading && error && (() => {
            // Draft Review is the last of three stages — point the user at
            // whichever of the other two they haven't done yet, rather than
            // always defaulting to Live Draft. `hasStrategy` undefined means
            // this isn't a "haven't gotten there yet" state at all but a
            // real failure (e.g. a snapshot that failed to read), so it gets
            // a neutral "back to the hub" CTA instead of a guess.
            const { hasStrategy } = error
            const cta = hasStrategy
              ? { label: 'Go to Live Draft', to: `/league/${id}/draft/live` }
              : hasStrategy === false
                ? { label: 'Go to Strategy Establishment', to: `/league/${id}/draft/strategy` }
                : { label: 'Go to Draft Room', to: `/league/${id}/draft` }
            const heading = hasStrategy === undefined ? "Couldn't load your draft review" : "Your draft isn't ready to review yet"

            return (
              <V2Card>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <JAIMascot size={64} state="idle" />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: V2.navy, letterSpacing: '0.03em', textTransform: 'uppercase', marginTop: 12, marginBottom: 4 }}>{heading}</div>
                  <div style={{ fontSize: 12.5, color: V2.mute, marginBottom: 14 }}>{error.message}</div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {/* A draft that was only just finished on the platform (or a
                        Starter tracker finished by hand) is found by asking again:
                        the server checks the platform when this is pressed. */}
                    {hasStrategy && (
                      <button
                        onClick={load} data-testid="review-check-again"
                        style={{ background: V2.blue, color: 'white', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Check again
                      </button>
                    )}
                    <button
                      onClick={() => navigate(cta.to)}
                      style={hasStrategy
                        ? { background: 'white', color: V2.navy, border: `1px solid ${V2.hairSoft}`, borderRadius: 9, padding: '9px 18px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }
                        : { background: V2.blue, color: 'white', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                    >
                      {cta.label} &rarr;
                    </button>
                  </div>
                </div>
              </V2Card>
            )
          })()}

          {!loading && !error && data && (
            <>
              <OverviewBlock overall={data.review.overall} leagueId={id} leagueType={data.leagueType} />
              <V2List>
                {data.review.picks.map((pick, i) => (
                  <ReviewPickRow key={pick.overallPick} pick={pick} sport={sport} leagueId={id} defaultOpen={i === 0} />
                ))}
              </V2List>
              <StickyAskBar leagueId={id} scope="review" scopeId={null} placeholder="Ask JAI about your draft" />
            </>
          )}
        </div>
      </div>
    </AppShell>
  )
}
